import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft, FileEdit, Save } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import PageTitle from '../components/PageTitle';
import { useAuth } from '../contexts/AuthContext';
import { isStaffOrAbove } from '../lib/tier';
import { useSpellCheckOnSave } from '../hooks/useSpellCheckOnSave';
import SpellCheckDialog from '../components/SpellCheckDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: 'hero_tagline', label: 'Hero tagline', help: 'Big headline under the logo on the home page. Use line breaks to split across lines.' },
  { key: 'suggestions_intro', label: 'Suggestions intro', help: 'Paragraph above the community suggestions section.' },
  { key: 'roadmap_intro', label: 'Roadmap intro', help: 'Paragraph above the Upcoming Improvements section.' },
  { key: 'released_intro', label: 'Released intro', help: 'Paragraph shown when users expand the Released Improvements section.' },
];

const SiteSettingsAdmin = () => {
  const { user } = useAuth();
  const isStaff = isStaffOrAbove(user);

  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/site-settings`, { withCredentials: true });
      setForm(res.data || {});
    } catch (err) {
      toast.error('Failed to load site settings');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isStaff) fetch(); }, [fetch, isStaff]);

  // Hooks MUST run before any early return — keep the order stable across renders.
  const persist = useCallback(async (payload) => {
    setSaving(true);
    try {
      await axios.put(`${API}/site-settings`, payload, { withCredentials: true });
      setForm(payload);
      toast.success('Home page copy updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  }, []);

  const buildPayload = useCallback(() => form, [form]);
  const sc = useSpellCheckOnSave({ buildPayload, persist });

  if (!isStaff) return <Navigate to="/home" replace />;

  const save = (e) => {
    e.preventDefault();
    sc.save();
  };

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12" data-testid="site-settings-page">
      <Link to="/home" className="inline-flex items-center text-sm text-[#64748B] hover:text-[#020617] mb-6">
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to home
      </Link>
      <PageTitle
        title="Edit home copy"
        subtitle="All public-facing text on the home page lives here. Saves instantly when you click the button."
        className="mb-8"
      />

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0052FF]" /></div>
      ) : (
        <form onSubmit={save} className="space-y-6 bg-white border border-[#E2E8F0] rounded-lg p-6">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <Label htmlFor={`field-${f.key}`} className="text-sm font-semibold">{f.label}</Label>
              <p className="text-xs text-[#64748B] mt-1 mb-2">{f.help}</p>
              <Textarea
                id={`field-${f.key}`}
                value={form[f.key] || ''}
                onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                rows={f.key === 'hero_tagline' ? 3 : 4}
                data-testid={`field-${f.key}`}
              />
            </div>
          ))}
          <div className="pt-2">
            <Button type="submit" disabled={saving} className="bg-[#0052FF] text-white hover:bg-[#003ECC]" data-testid="site-settings-save">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        </form>
      )}
      <SpellCheckDialog {...sc.dialogProps} />
    </div>
  );
};

export default SiteSettingsAdmin;
